/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, Check } from '@fluentui/react';
import {
    DetailsHeader,
    DetailsRow,
    DetailsRowCheck,
    IColumn,
    IDetailsHeaderProps,
    IDetailsRowCheckProps,
    IDetailsRowProps,
    IDetailsRowStyles
} from '@fluentui/react/lib/DetailsList';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Icon } from '@fluentui/react/lib/Icon';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { classNamesFunction, IRenderFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisMigratedAppStore } from 'src/clu/core/stores/LuisMigratedAppStore';
import { useLuisToCluMigratedApps } from 'src/clu/modules/hooks/useLuisToCluMigratedApps';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import {
    CluMigrationContentSideMargin,
    CluMigrationInfoDto,
    CluMigrationSteps
} from 'src/luis/verticals/cluMigrationModal/CluMigrationModal';
import { CluMigrationModalFooter } from 'src/luis/verticals/cluMigrationModal/components/CluMigrationModalFooter';
import { LuisApplicationsTable } from 'src/luis/verticals/cluMigrationModal/components/LuisApplicationsTable';
import styled from 'styled-components';

export type CluMigrationModalSelectLuisApplicationsProps = {
    onDismiss: () => void;
    onPrevious: () => void;
    cluMigrationInfoDto: CluMigrationInfoDto;
    onSubmit: (luisApplications: LuisMigratedAppStore[]) => void;
};

const messages = defineMessages({
    title: {
        id: 'cluMigration.selectLuisApplications.title',
        defaultMessage: 'Select LUIS applications'
    },
    description1: {
        id: 'cluMigration.selectLuisApplications.description1',
        defaultMessage: 'Select the LUIS applications you would like to copy over to conversational language understanding.'
    },
    learnMoreAboutFeatures: {
        id: 'cluMigration.selectLuisApplications.learnMoreAboutFeatures',
        defaultMessage: 'Learn more about the differences between the two services.'
    },
    description2: {
        id: 'cluMigration.selectLuisApplications.description2',
        defaultMessage: 'You must select the version you wish to copy over for each project. You can only migrate one version per project.'
    },
    description3: {
        id: 'cluMigration.selectLuisApplications.description3',
        defaultMessage: 'Application names will be transferred following rules documented'
    },
    here: {
        id: 'cluMigration.selectLuisApplications.here',
        defaultMessage: 'here.'
    },
    name: {
        id: 'cluMigration.selectLuisApplications.name',
        defaultMessage: 'Name'
    },
    culture: {
        id: 'cluMigration.selectLuisApplications.culture',
        defaultMessage: 'Culture'
    },
    version: {
        id: 'cluMigration.selectLuisApplications.version',
        defaultMessage: 'Version'
    },
    search: {
        id: 'cluMigration.selectLuisApplications.search',
        defaultMessage: 'Search'
    },
    includes: {
        id: 'cluMigration.selectLuisApplications.includes',
        defaultMessage: 'Your application includes'
    },
    patterns: {
        id: 'cluMigration.selectLuisApplications.patterns',
        defaultMessage: 'patterns'
    },
    patternAnyEntities: {
        id: 'cluMigration.selectLuisApplications.patternAnyEntities',
        defaultMessage: 'patterns.any entities'
    },
    phraseListFeatures: {
        id: 'cluMigration.selectLuisApplications.phraseListFeatures',
        defaultMessage: 'phrase list features'
    },
    entities: {
        id: 'cluMigration.selectLuisApplications.entities',
        defaultMessage: 'entities as a feature to intents'
    },
    versions: {
        id: 'cluMigration.selectLuisApplications.versions',
        defaultMessage: 'versions'
    },
    structuredEntities: {
        id: 'cluMigration.selectLuisApplications.structuredEntities',
        defaultMessage: 'structured entities.'
    },
    features: {
        id: 'cluMigration.selectLuisApplications.features',
        defaultMessage: 'These features will not be transferred to conversational language understanding.'
    },
    leafNodes: {
        id: 'cluMigration.selectLuisApplications.leafNodes',
        defaultMessage: 'The leaf nodes of your structure will be added as flat entities to conversational language understanding.'
    },
    learnMore: {
        id: 'cluMigration.selectLuisApplications.learnMore',
        defaultMessage: 'Learn more'
    }
});

const localTheme = getScopedTheme('CluMigrationModalSelectLuisApplicationsScreen');

const RowContainer = styled(Stack)<{ isSelected: boolean }>(props => ({
    borderBottom: `${localTheme.focusingColor} solid 1px`,
    $nest: {
        '&:hover': { backgroundColor: `${props.isSelected ? localTheme.focusingColor : localTheme.hoveringColor}` }
    }
}));

const detailsRowStyles = classNamesFunction<IDetailsRowProps, IDetailsRowStyles>()({
    root: {
        borderBottom: 'none',
        backgroundColor: 'transparent !important',
        userSelect: 'auto'
    },
    cell: {
        overflow: 'visible'
    },
    check: {
        cursor: 'pointer !important'
    }
});

export const SelectLuisApplicationsScreen = observer((props: CluMigrationModalSelectLuisApplicationsProps) => {
    const { cluMigrationInfoDto, onSubmit, onDismiss, onPrevious } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isLargeOrGreater } = useResponsiveMode();

    const { cluMigrationStoreHouse } = rootStore;
    const { luisMigratedAppsStoreHouse } = cluMigrationStoreHouse;

    const { loadAppVersionData, loadAppVersions, setAppSelectedVersion, isLoading, luisAppsTracker } = useLuisToCluMigratedApps(
        cluMigrationInfoDto.applications
    );

    const [searchQuery, setSearchQuery] = React.useState('');

    const [sortKey, setSortKey] = React.useState<SortKeyType<LuisMigratedAppStore>>({ key: 'name', order: 'asc' });

    const [selectedItems, setSelectedItems] = React.useState([]);

    React.useEffect(() => {
        setSelectedItems([...cluMigrationInfoDto.applications.map(app => app.id)]);
    }, [cluMigrationInfoDto]);

    const filteredApplications = React.useMemo(
        () => orderBy(substringSearchStrategy(luisMigratedAppsStoreHouse.items, searchQuery, ['name']), sortKey.key, [sortKey.order]),
        [searchQuery, luisMigratedAppsStoreHouse.items, sortKey]
    );

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(filteredApplications, 10);

    const isAllPageItemsSelected = React.useMemo(
        () => pageItems.length && pageItems.map(item => item.id).every(id => selectedItems.includes(id)),
        [pageItems]
    );
    const [allItemsSelected, setAllItemsSelected] = React.useState(isAllPageItemsSelected);

    React.useEffect(() => setAllItemsSelected(isAllPageItemsSelected), [pageItems]);

    React.useEffect(() => {
        if (selectedItems.length && luisAppsTracker.isCompleted) {
            selectedItems.forEach(id => {
                const tmpApp = luisMigratedAppsStoreHouse.findById(id);
                if (!isVersionLoaded(id, tmpApp.selectedVersion)) {
                    loadAppVersionData(id, tmpApp.selectedVersion);
                }
            });
        }
    }, [selectedItems, luisAppsTracker.isCompleted]);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleColumnClick = (c: IColumn) => {
        setSortKey({ key: c.key as keyof LuisMigratedAppStore, order: c.isSortedDescending ? 'asc' : 'desc' });
    };

    const isVersionLoaded = (appId: string, versionId: string) =>
        luisMigratedAppsStoreHouse.findById(appId).loadedVersions.includes(versionId);

    const getWarningMessage = (appId: string, versionId: string) => {
        const app = luisMigratedAppsStoreHouse.findById(appId).luisApp;
        const appVersion = app.appVersionStoreHouse.findById(versionId);

        if (!appVersion) {
            return null;
        }

        const message: string[] = [];

        if (appVersion.patternStoreHouse.items.length > 0) {
            message.push(localize(messages.patterns));
        }

        if (appVersion.entityStoreHouse.patternAnyEntities.length > 0) {
            message.push(localize(messages.patternAnyEntities));
        }

        if (appVersion.phraseListStoreHouse.items.length > 0) {
            message.push(localize(messages.phraseListFeatures));
        }

        if (appVersion.intentStoreHouse.items.some(item => item.featureStoreHouse.items.some(it => it.typeId === 'model'))) {
            message.push(localize(messages.entities));
        }

        if (app.appVersionStoreHouse.items.length > 1) {
            message.push(localize(messages.versions));
        }

        if (message.length) {
            return (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 20, padding: '10px 0 20px 40px' }}>
                    <Icon iconName="Warning12" styles={{ root: { color: localTheme.warningColor, fontSize: '14px' } }} />
                    <Stack horizontalAlign="center" tokens={{ childrenGap: 5 }}>
                        {appVersion.entityStoreHouse.simpleEntities.some(item => item.children.length) && (
                            <LuisText>
                                <LuisText variant="small">{localize(messages.includes)}</LuisText>
                                &nbsp;
                                <LuisText fontWeight={600} variant="small">
                                    {localize(messages.structuredEntities)}
                                </LuisText>
                                &nbsp;
                                <LuisText variant="small">{localize(messages.leafNodes)}</LuisText>
                            </LuisText>
                        )}
                        <LuisText variant="small">
                            <LuisText variant="small">{localize(messages.includes)}</LuisText>
                            &nbsp;
                            <LuisText fontWeight={600} variant="small">
                                {message.join(', ').replace(/,(?=[^,]*$)/, ' and')}.
                            </LuisText>
                            &nbsp;
                            <LuisText variant="small">{localize(messages.features)}</LuisText>
                            &nbsp;
                            <ExternalLink href="https://aka.ms/clu-backwards-compatibility">{localize(messages.learnMore)}</ExternalLink>
                        </LuisText>
                    </Stack>
                </Stack>
            );
        }

        return null;
    };

    const toggleCheckbox = (id: string) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(item => item !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const toggleAllCheckbox = () => {
        if (allItemsSelected) {
            const selectedPageItems = selectedItems.filter(id => pageItems.map(item => item.id).includes(id));
            setSelectedItems([...selectedItems.filter(id => !selectedPageItems.includes(id))]);
        } else {
            const notSelectedPageItems = pageItems.map(item => item.id).filter(id => !selectedItems.includes(id));
            setSelectedItems([...selectedItems, ...notSelectedPageItems]);
        }
        setAllItemsSelected(!allItemsSelected);
    };

    const onRenderHeader: IRenderFunction<IDetailsHeaderProps> = headerProps => (
        <>
            <DetailsHeader
                {...headerProps}
                onRenderDetailsCheckbox={p => (
                    <ActionButton onClick={toggleAllCheckbox}>
                        <Check {...p} checked={allItemsSelected} />
                    </ActionButton>
                )}
            />
        </>
    );

    const onRenderCheckbox = (rowProps: IDetailsRowCheckProps, id: string) => (
        <DetailsRowCheck
            {...rowProps}
            onClick={() => toggleCheckbox(id)}
            selected={selectedItems.includes(id)}
            styles={{ root: { cursor: 'default' } }}
        />
    );

    const onRenderRow: IRenderFunction<IDetailsRowProps> = rowProps => (
        <RowContainer id={`${rowProps.itemIndex}`} tokens={{ childrenGap: 10 }} isSelected={selectedItems.includes(rowProps.item.id)}>
            <DetailsRow onRenderCheck={p => onRenderCheckbox(p, rowProps.item.id)} {...rowProps} styles={detailsRowStyles} />
            {selectedItems.includes(rowProps.item.id) && (
                <>
                    {isVersionLoaded(rowProps.item.id, rowProps.item.selectedVersion) ? (
                        <>{getWarningMessage(rowProps.item.id, rowProps.item.selectedVersion)}</>
                    ) : (
                        <Stack horizontalAlign="center" tokens={{ padding: '10px 0 20px 40px' }}>
                            <Spinner size={SpinnerSize.medium} />
                        </Stack>
                    )}
                </>
            )}
        </RowContainer>
    );

    const onVersionChange = (item: LuisMigratedAppStore, option: IDropdownOption) => {
        if (selectedItems.includes(item.id) && !isVersionLoaded(item.id, option.key as string)) {
            loadAppVersionData(item.id, option.key as string);
        }
        setAppSelectedVersion(item.id, option.key as string);
    };

    const setVersionDropdownOptions = (appId: string) => {
        const tmpApp = luisMigratedAppsStoreHouse.findById(appId);

        if (tmpApp.areVersionsLoading) {
            return [];
        }

        return tmpApp.luisApp.appVersionStoreHouse.items.length
            ? tmpApp.luisApp.appVersionStoreHouse.items.map(version => ({
                  key: version.id,
                  text: version.id,
                  selected: version.id === tmpApp.selectedVersion
              }))
            : [{ key: tmpApp.selectedVersion, text: tmpApp.selectedVersion }];
    };

    const renderDescription = () => (
        <Stack tokens={{ childrenGap: 10 }}>
            <LuisText>
                {localize(messages.description1)}
                &nbsp;
                <ExternalLink href="https://aka.ms/clu-backwards-compatibility">{localize(messages.learnMoreAboutFeatures)}</ExternalLink>
            </LuisText>
            <LuisText>{localize(messages.description2)}</LuisText>
            <LuisText>
                {localize(messages.description3)}
                &nbsp;
                <ExternalLink href="https://aka.ms/clu-name-rules">{localize(messages.here)}</ExternalLink>
            </LuisText>
        </Stack>
    );

    const farItems = [
        {
            key: 'search',
            onRender: () => (
                <LuisDebouncedSearchBox
                    role="menuitem"
                    value={searchQuery}
                    size={isLargeOrGreater ? 24 : 20}
                    ariaLabel={localize(messages.search)}
                    styles={{ root: { marginTop: '5px' } }}
                    placeholder={localize(messages.search)}
                    onChange={(_, newValue?: string) => setSearchQuery(newValue)}
                />
            )
        }
    ];

    return (
        <Stack>
            <Stack
                styles={{ root: { height: '150px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `0 0 0 ${CluMigrationContentSideMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    {renderDescription()}
                </Stack>
            </Stack>
            <LuisCommandBar items={[]} farItems={farItems} styles={{ root: { paddingRight: 5 } }} borderPosition="Bottom" />
            <Stack.Item tokens={{ padding: `0 0 0 ${CluMigrationContentSideMargin}` }}>
                <LuisApplicationsTable
                    sortKey={sortKey}
                    items={pageItems}
                    pageCount={pageCount}
                    onRenderRow={onRenderRow}
                    onSortColumn={handleColumnClick}
                    onVersionChange={onVersionChange}
                    loadAppVersions={loadAppVersions}
                    isLoading={luisAppsTracker.isRunning}
                    onRenderDetailsHeader={onRenderHeader}
                    setVersionDropdownOptions={setVersionDropdownOptions}
                />
                <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => handlePageChange(p.selected)} />
            </Stack.Item>
            <CluMigrationModalFooter
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                isValid={selectedItems.length > 0 && !isLoading()}
                activeStep={CluMigrationSteps.SelectLuisApplications}
                onNext={() => onSubmit(selectedItems.map(id => luisMigratedAppsStoreHouse.findById(id)))}
            />
        </Stack>
    );
});
